import { Socket, io } from "socket.io-client";
import SocketProtocol from "./SocketProtocol";
import { PlayerState } from "module/types";
import UserSession from "./UserSession";
import AppConfig from "config/AppConfig";

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private readonly config = AppConfig.getInstance();
  private connectionListeners: ((isConnected: boolean) => void)[] = [];

  // 재연결 관련 설정
  private readonly reconnectAttempts = 5; // 최대 재시도 횟수
  private readonly reconnectTimeout = 5000; // 재연결 시도 간격 (ms)
  private readonly connectionTimeout = 10000; // 연결 타임아웃 (ms)
  private currentAttempt = 0;

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  private constructor() {
    // Initialize the socket immediately upon instance creation
    // this.connect();

    // Page Visibility API for mobile screen on/off detection
    this.setupVisibilityListener();
  }

  private setupVisibilityListener(): void {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          console.log("Page became visible, checking connection...");
          // 화면 켜짐 시 연결 상태 확인 및 재연결
          if (this.socket && !this.socket.connected) {
            console.log("Connection lost during hidden state, reconnecting...");
            this.socket.connect();
          }
        } else {
          console.log("Page became hidden");
        }
      });
    }
  }

  private setCookie(name: string, value: string, exp: number) {
    var date = new Date();
    date.setTime(date.getTime() + exp * 24 * 60 * 60 * 1000);
    document.cookie =
      name + "=" + value + ";expires=" + date.toUTCString() + ";path=/";
  }

  private getCookie(name: string) {
    var value = document.cookie.match("(^|;) ?" + name + "=([^;]*)(;|$)");
    return value ? value[2] : null;
  }

  public isConnected(): boolean {
    return this.socket?.connected ?? true;
  }

  public addConnectionListener(listener: (isConnected: boolean) => void): void {
    this.connectionListeners.push(listener);
    // Don't immediately notify - let the actual connection state be determined
  }

  public removeConnectionListener(
    listener: (isConnected: boolean) => void
  ): void {
    this.connectionListeners = this.connectionListeners.filter(
      (l) => l !== listener
    );
  }

  private notifyConnectionListeners(): void {
    const isConnected = this.isConnected();

    this.connectionListeners.forEach((listener) => listener(isConnected));
  }

  public connect(): void {
    if (!this.socket) {
      const socketOptions = {
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: Infinity, // 무제한 재연결 시도
        reconnectionDelay: 1000, // 1초 대기
        reconnectionDelayMax: 5000, // 최대 5초
        timeout: 20000, // 20초 타임아웃 (모바일 네트워크 고려)
        transports: ["websocket", "polling"], // polling fallback 추가
      };

      this.socket = io(this.config.getServerUrl(), socketOptions);

      this.socket.on("connect", () => {
        console.log("Socket connected:", this.socket?.id);
        let sessionId = this.getCookie("sessionId");
        if (!sessionId) {
          this.setCookie("sessionId", this.socket?.id!, 1);
        }
        this.notifyConnectionListeners();
      });

      this.socket.on("connect_error", (error) => {
        console.log("Socket connection error:", error);
        this.notifyConnectionListeners();
      });

      this.socket.on("disconnect", () => {
        console.log("Socket disconnected");
        this.notifyConnectionListeners();
      });
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public emit(event: string, ...args: any[]): void {
    if (this.socket) {
      this.socket.emit(event, ...args);
    }
  }

  public on(event: string, func: (...args: any[]) => void): void {
    if (this.socket) {
      this.socket.on(event, func);
    }
  }

  public listenError() {
    if (this.socket) {
      this.socket.on("error", (error) => {
        console.log("On Error " + error);
      });
    }
  }

  public off(event: string, func?: (...args: any[]) => void): void {
    if (this.socket) {
      this.socket.off(event, func);
      this.socket.off(event);
    }
  }

  public sendChangeState(
    recreationId: number,
    roomName: string,
    state: PlayerState
  ) {
    this.emit(
      SocketProtocol.CHANGE_PLAYER_STATE,
      recreationId,
      roomName,
      UserSession.getInstance().user.id,
      state
    );
  }
}
export default SocketService;
